<template>
  <div class="wt-upload-view">
    <wt-files-dropzone :show="isDragging">
      <template slot="indicator">
        <i class="wticon wticon-upload2" />
        <span class="-drop-text">{{
          'generic.DND_INVITATION_M1' | culture
        }}</span>
        <span class="-drop-formats">{{
          'generic.DND_INVITATION_M2' | culture
        }}</span>
      </template>
    </wt-files-dropzone>

    <input
      v-show="false"
      type="file"
      ref="file"
      @change="onFileChange"
      accept="*"
      multiple
    />

    <header>
      <div class="wt2-text-l3">
        <template v-if="allowAudioFiles">
          {{ 'uploadView.title.fileUploadModal_All' | culture }}
        </template>
        <template v-else>
          {{ 'uploadView.title.fileUploadModal_NoAudio' | culture }}
        </template>
      </div>
    </header>
    <main>
      <div v-if="isEmpty" class="-panel">
        <div class="-empty-files">
          <div class="-center">
            <span class="-a wticon wticon-upload2" />
            <div class="-b wt2-text-m">
              Drop files or
              <a href="#" @click.prevent="onBrowse" class="wt2-link">browse</a>
            </div>
            <div class="-c wt2-text-s">
              {{ 'uploadView.supportedMedia' | culture }}
              <template v-if="allowAudioFiles">
                , {{ 'uploadView.supportedAudio' | culture }}
              </template>
              <br />
              <a
                class="wt2-link"
                v-tooltip.extend="{
                  title: $options.supportedTooltip,
                  html: true,
                  placement: 'top',
                  delay: 0,
                  wrapperClass: 'file-upload-wrapper',
                }"
                >Supported formats</a
              >
            </div>
          </div>
        </div>
      </div>
      <div v-else class="-panel -file-panel">
        <div class="-head">
          <div class="wt2-text-n2">
            {{ 'uploadView.filePanelHead' | culture }}
          </div>
          <a href="#" @click.prevent="onBrowse" class="wt2-link">Add</a>
        </div>
        <ul class="-file-list">
          <li v-if="displayedMediaFiles.length" class="wt2-text-s -title">
            Visual
          </li>
          <file-list-record
            v-for="file in displayedMediaFiles"
            :key="file.guid"
            class="-visual"
            :file="file"
            @remove-file-record="onRemoveFileRecord"
          />
          <li v-if="displayedNarrationFiles.length" class="wt2-text-s -title">
            Audio
          </li>
          <file-list-record-audio
            v-for="file in displayedNarrationFiles"
            :key="file.guid"
            class="-audio"
            :file="file"
            @remove-file-record="onRemoveFileRecord"
          />
        </ul>
        <div v-if="hasProblem" class="-file-error">
          {{ 'uploadView.genericWarning' | culture }}
          <a
            href="#"
            class="wt2-link"
            v-tooltip.extend="{
              title: $options.supportedTooltip,
              html: true,
              placement: 'top',
              delay: 0,
              wrapperClass: 'file-upload-wrapper',
            }"
            >Supported Formats</a
          >
        </div>
        <div v-if="!isEmpty" class="-support">
          {{ 'uploadView.supportedMedia' | culture }},
          {{ 'uploadView.supportedAudio' | culture }}
        </div>
      </div>

      <template v-if="!isEmpty">
        <aside class="-panel-divider" />
        <div
          class="-panel"
          :class="{ '-disabled': !displayedMediaFiles.length }"
        >
          <section class="wt2-text-n2">
            {{ 'uploadView.mediaInfo' | culture }}
          </section>
          <section>
            <wt-toggle
              class="-privacy-label"
              :label-left="'uploadView.privacyLabel' | culture"
              v-model="model.sharedWithTeam"
            />
          </section>
          <section>
            <wt-toggle
              v-if="hasVideoFile"
              class="-privacy-label"
              :label-left="'uploadView.transcribeLabel' | culture"
              v-model="model.autoTranscribe"
            />
          </section>
          <section>
            <wt-text-field
              v-model="model.caption"
              :placeholder="'uploadView.captionPlaceholder' | culture"
              :mandatory="false"
              :maxlength="MAX_TITLE_LENGTH"
            />
          </section>
          <section>
            <wt-text-field
              v-model="model.attribution"
              :placeholder="'uploadView.attributionPlaceholder' | culture"
              :mandatory="false"
              :maxlength="MAX_TITLE_CREDIT_LENGTH"
            />
            <wt-check-box
              class="-attribution-box wt2-text-s"
              v-model="model.attributionMode"
              :disabled="!model.attribution"
              :label-right="'uploadView.attributionMode' | culture"
            />
          </section>
          <section>
            <textarea
              class="wt2-textarea -description"
              v-model="model.description"
              :placeholder="'uploadView.descriptionPlaceholder' | culture"
              :maxlength="MAX_DESCRIPTION_LENGTH"
              rows="3"
            />
          </section>
          <section>
            <wt-add-keywords
              theme="black-theme"
              :keyword-height="31"
              :maxRows="4"
              :keywords="model.keywords"
              emptyKeywordsPlaceholder="Add tags"
              @addkeyword="addKeyword"
              @removekeyword="removeKeyword"
            />
          </section>
        </div>
      </template>
    </main>
    <footer>
      <wt-button
        type="primary"
        text="Upload"
        @click="onDone"
        :disabled="denyUpload"
      />
      <wt-button type="transparent" text="Cancel" @click="closeModal" />
      <div class="-info-panel">
        <div v-if="shouldRedirect" class="-bottomed">
          {{ 'uploadView.redirectWarning' | culture }}
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import app from 'app';
import UploadMixin from './UploadMixin.vue';
import FileListRecord from './FileListRecord.vue';
import FileListRecordAudio from './FileListRecordAudio.vue';
import { mapState } from 'vuex';
import Analytics from 'c/WtAnalytics';
import {
  getFileExtension,
  extensionToMimeType,
  mimeType,
} from '../../utils/file-api';
import { SIZE } from '#/const.ts';

export default {
  mixins: [UploadMixin],
  components: { FileListRecord, FileListRecordAudio },

  props: {
    closeModal: { type: Function },
    analyticsData: { type: Object },
    validateVideoDuration: { type: Boolean, default: false },
    initEvent: { type: [Object, Event], required: false, default: null },
    allowAudioFiles: { type: Boolean, required: false, default: true },
  },

  analytics: {
    upload: 'Upload File',
  },

  data() {
    return {
      guid: app.guid(),
      model: {
        caption: '',
        description: '',
        files: [],
        // ... other fields in UploadMixin
      },
      hasProblem: false,
      longUploadPredicted: false,
      isDragging: false,
    };
  },

  computed: {
    ...mapState({
      storyId: (state) => state.story.id,
    }),

    fileCaption() {
      return this.model.caption.normalizeSpace();
    },

    MAX_DESCRIPTION_LENGTH() {
      return 1000;
    },

    isEmpty() {
      return _.isEmpty(this.model.files);
    },

    isValidating() {
      return _.some(this.model.files, (file) => {
        return file.cpValid.isPending();
      });
    },

    denyUpload() {
      return (
        _.isEmpty(this.model.files) || this.isValidating || this.hasProblem
      );
    },

    shouldRedirect() {
      return !this.hasProblem && this.longUploadPredicted;
    },

    displayedNarrationFiles() {
      return this.displayedFiles.filter(this.isNarration);
    },

    displayedMediaFiles() {
      return this.displayedFiles.filter((f) => !this.isNarration(f));
    },

    displayedFiles() {
      return _.orderBy(this.model.files, 'lastModified', false /* desc*/);
    },

    hasVideoFile() {
      return _.some(this.model.files, (file) => {
        return mimeType.isVideo(file.type);
      });
    },
  },

  beforeDestroy() {
    app.namespace.Util.FileDndDispatcher.remove(this.guid);
  },

  created() {
    const supportedMediaExtensions = [...tk.context._supportedFileExtensions];
    const supportedAudioExtensions = this.allowAudioFiles
      ? [...app.DEFAULT.SUPPORTED_AUDIO_EXTENSIONS]
      : [];
    const allExtensions = [
      ...supportedMediaExtensions,
      ...supportedAudioExtensions,
    ];
    const mimeTypes = app.DEFAULT.BROADLY_INCLUSIVE_MEDIA_MIMES;

    Object.assign(this.$options, {
      mimeTypes,
      supportedMediaExtensions,
      supportedAudioExtensions,
      supportedTooltip: `
            <div class="wt2-text-n2" style="margin-bottom: 5px;">
                ${app.culture.get('uploadView.supportTooltipTitle')}
            </div>
            ${allExtensions.join(', ')}
            <div style="margin-top: 10px;">${app.culture.get(
              'uploadView.supportTooltipFooter'
            )}</div>
        `.normalizeSpace(),
    });
  },

  mounted() {
    if (this.$props.initEvent) {
      this.onFileChange(this.$props.initEvent);
    }

    app.namespace.Util.FileDndDispatcher.add({
      guid: this.guid,
      handlers: {
        dragover: this.$_dragOver,
        dragenter: this.$_dragEnter,
        dragleave: this.$_dragLeave,
        drop: this.$_drop,
        paste: this.$_onPaste,
      },
    });
  },

  watch: {
    'model.files': {
      deep: false,
      handler() {
        // revalidate files as a whole on each change of array
        const toValidate = [];
        _.each(this.model.files, (file) => {
          // add everything to validation queue
          toValidate.push(file.cpValid);
          // start validating new ones (if has)
          if (file.cpValid.isPending()) {
            if (mimeType.isVideo(file.type)) {
              this.validateVideoFile(file);
            } else if (mimeType.isImage(file.type)) {
              this.validateImageFile(file);
            } else if (mimeType.isAudio(file.type)) {
              this.validateAudioFile(file);
            } else {
              const ext = getFileExtension(file.name);
              file.cpValid.reject(
                app.culture.get('uploadView.fileError.format')(ext)
              );
            }
          }
        });

        if (_.isEmpty(toValidate)) {
          this.hasProblem = false;
          this.longUploadPredicted = false;
        } else {
          Promise.all(toValidate).then(
            (result) => {
              this.hasProblem = false;
              this.longUploadPredicted = _.some(
                result,
                (status) => status && status.isTooLong
              );
            },
            () => {
              this.hasProblem = true;
            }
          );
        }
      },
    },
  },

  methods: {
    onBrowse() {
      this.$refs.file.value = null;
      this.$refs.file.click();
    },

    onFileChange(e) {
      const fileList = app.getFilesFromEvent(e);
      const newFiles = [];

      // here not working with File object but constructing a wrapper
      _.each(fileList, (file) => {
        const guid = `${file.size}-${file.lastModified}-${file.type}-${file.name}`;

        // some systems don't detect mimetype of some files, so here we assign it forcibly
        // for every supported extension
        const extension = getFileExtension(file.name);
        const fileType = extensionToMimeType(extension);

        if (_.isEmpty(fileType)) {
          app.doctorController.report(
            `FileUpload empty mimeType: '${extension}'`
          );
        }

        if (!_.some(this.model.files, (f) => guid === f.guid)) {
          // add only unique files
          const newFile = {
            name: file.name || 'noname',
            lastModified: file.lastModified,
            size: file.size,
            type: fileType,
            blob: file,
            // view specific fields
            guid: guid,
            error: null,
            cpValid: new _.ColdPromise(),
          };
          newFiles.unshift(newFile);
          newFile.cpValid.catch((error) => {
            newFile.error = error;
          });
        }
      });

      if (!_.isEmpty(newFiles)) {
        this.model.files = [...newFiles, ...this.model.files];
      }
    },

    onRemoveFileRecord({ file }) {
      if (file.cpValid.isPending()) {
        file.cpValid.cancel();
      }

      const i = this.model.files.indexOf(file);
      if (i >= 0) {
        this.model.files.splice(i, 1);
      }
    },

    validateAudioFile(file) {
      if (file.size < SIZE.MIN_UPLOAD) {
        file.cpValid.reject(app.culture.get('uploadView.fileError.empty'));
        return;
      } else if (file.size > SIZE.MAX_UPLOAD_AUDIO) {
        file.cpValid.reject(app.culture.get('uploadView.fileError.bigAudio'));
        return;
      }

      const ext = getFileExtension(file.name);
      if (!_.some(this.$options.supportedAudioExtensions, (d) => d === ext)) {
        file.cpValid.reject(
          app.culture.get('uploadView.fileError.format')(ext)
        );
      }
      file.cpValid.resolve();
    },

    validateImageFile(file) {
      if (file.size < SIZE.MIN_UPLOAD) {
        file.cpValid.reject(app.culture.get('uploadView.fileError.empty'));
        return;
      } else if (file.size > SIZE.MAX_UPLOAD) {
        file.cpValid.reject(app.culture.get('uploadView.fileError.bigMedia'));
        return;
      }

      const ext = getFileExtension(file.name);
      if (!_.some(this.$options.supportedMediaExtensions, (d) => d === ext)) {
        file.cpValid.reject(
          app.culture.get('uploadView.fileError.format')(ext)
        );
        return;
      }
      file.cpValid.resolve();
    },

    validateVideoFile(file) {
      if (file.size < SIZE.MIN_UPLOAD) {
        file.cpValid.reject(app.culture.get('uploadView.fileError.empty'));
        return;
      } else if (file.size > SIZE.MAX_UPLOAD) {
        file.cpValid.reject(app.culture.get('uploadView.fileError.bigMedia'));
        return;
      }

      const ext = getFileExtension(file.name);
      if (!_.some(this.$options.supportedMediaExtensions, (d) => d === ext)) {
        file.cpValid.reject(
          app.culture.get('uploadView.fileError.format')(ext)
        );
        return;
      }

      const video = document.createElement('video');
      video.preload = 'metadata';

      video.addEventListener('error', (e) => {
        if (this.$props.validateVideoDuration) {
          file.cpValid.resolve({ isTooLong: true });
        } else {
          file.cpValid.resolve();
        }
      });

      video.addEventListener('loadedmetadata', () => {
        file.duration = video.duration;
        if (this.$props.validateVideoDuration) {
          file.cpValid.resolve({
            isTooLong: file.duration > app.MAX_LIGHTBOX_VIDEO_DURATION,
          });
        } else {
          file.cpValid.resolve();
        }
      });

      video.src = URL.createObjectURL(file.blob);
      file.cpValid.finally(() => {
        URL.revokeObjectURL(video.src);
        video.src = '';
      });
    },

    isNarration(file) {
      return (
        !_.isEmpty(file.type) &&
        app.DEFAULT.EXCLUSIVE_AUDIO_MIMES.includes(file.type)
      );
    },

    onDone() {
      const uqModels = [];
      const audioFiles = this.displayedNarrationFiles.length;
      const visualFiles = this.displayedMediaFiles.length;
      const analyticsData = Object.assign(
        {
          using: 'unknown source',
        },
        this.analyticsData
      );

      // Analytics
      if (audioFiles) {
        this.$triggerAnalytics('upload', {
          numberOfFiles: audioFiles,
          type: 'audio',
          ...analyticsData,
        });
      }
      if (visualFiles) {
        this.$triggerAnalytics('upload', {
          numberOfFiles: visualFiles,
          type: 'visual',
          ...analyticsData,
        });
      }

      for (let file, i = 0, I = this.model.files.length; i < I; i++) {
        file = this.model.files[this.model.files.length - i - 1];
        // remove extension
        let title = _.capitalize(file.name.replace(/(.*)\.[^.]+$/g, '$1'));
        if (this.fileCaption.length) {
          if (I > 1) {
            title = `${this.fileCaption}_${i + 1}`;
          } else {
            title = this.fileCaption;
          }
        }

        const isNarration = this.isNarration(file);
        const isVideo = mimeType.isVideo(file.type);
        let model;
        const data = Object.assign({}, this.commonData, {
          title,
          fileName: file.name,
          metadata: {
            assetUUID: app.guid(),
            uploadStatus: app.uploadStatus.UPLOAD,
            caption: this.model.description.normalizeSpace(),
          },
          progress: {},
          blob: file.blob,
          mimeType: file.blob.type,
        });

        if (isVideo) {
          data.duration = file.duration;

          if (this.model.autoTranscribe) {
            Analytics.trigger('VO:autoTranscribeSelfUpload', {
              Type: 'Upload',
              'Asset duration': file.duration
                ? app.moment.utc(file.duration * 1e3).format('m:ss')
                : null,
            });
          }
        }

        if (isNarration) {
          data.mediaAssetSubType = app.mediaAssetSubType.NARRATION;
          data.type = app.assetType.AUDIO;

          model = new app.namespace.Model.UploadQueueModel(data, {
            uploadType: app.uploadType.NARRATION_ASSET,
            storyId: this.storyId,
          });
        } else {
          model = new app.namespace.Model.UploadQueueModel(data);
        }

        app.UploadQueue.upload(model);
        uqModels.push(model);
      }

      this.closeModal({
        uqModels,
        redirectToAssetGallery: this.shouldRedirect,
      });
    },

    $_dragOver(e) {
      const dt = e.dataTransfer;
      if (dt.types && dt.types.indexOf('Files') >= 0) {
        e.preventDefault();
        e.stopPropagation();
        dt.dropEffect = 'copy';
      } else {
        this.isDragging = false;
      }
    },

    $_dragEnter: function (e) {
      e.preventDefault();
      if (e.fromElement === null) {
        this.isDragging = true;
      }
    },

    $_dragLeave: function (e) {
      e.preventDefault();
      if (e.fromElement === null) {
        this.isDragging = false;
      }
    },

    $_drop: function (e) {
      e.preventDefault();
      this.isDragging = false;

      this.onFileChange(e);
    },

    $_onPaste: function (e) {
      this.onFileChange(e);
    },
  },
};
</script>
